/**
 * Runtime Environment Configuration Service
 * Provides typed access to environment variables from Vite + .env files
 */

import type { RuntimeEnv, FeatureFlags } from '@/types'

class RuntimeEnvironment {
  private env: RuntimeEnv

  constructor() {
    this.env = this.loadEnv()
  }

  private loadEnv(): RuntimeEnv {
    return {
      pbUrl: this.getEnvString('VITE_PB_URL', 'http://localhost:8090'),
      collectionOverrides: this.loadCollectionOverrides(),
      appEnv: (this.getEnvString('VITE_APP_ENV', 'development') as 'development' | 'staging' | 'production'),
      logLevel: (this.getEnvString('VITE_LOG_LEVEL', 'info') as 'debug' | 'info' | 'warn' | 'error'),
      appVersion: this.getEnvString('VITE_APP_VERSION', '0.1.0'),
      buildTime: this.getEnvString('VITE_BUILD_TIME', new Date().toISOString()),
      features: this.loadFeatureFlags(),
    }
  }

  private loadFeatureFlags(): FeatureFlags {
    return {
      offlineQueue: this.getEnvBool('VITE_FEATURE_OFFLINE_QUEUE', true),
      errorTracking: this.getEnvBool('VITE_FEATURE_ERROR_TRACKING', true),
      performanceMonitoring: this.getEnvBool('VITE_FEATURE_PERFORMANCE_MONITORING', true),
    }
  }

  private loadCollectionOverrides(): Partial<Record<string, string>> {
    const raw = this.getEnvString('VITE_COLLECTION_OVERRIDES', '{}').trim()
    if (!raw) return {}

    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const cleaned: Record<string, string> = {}

      Object.entries(parsed).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim()) {
          cleaned[key] = value.trim()
        }
      })

      return cleaned
    } catch {
      if (this.shouldLog('warn')) {
        console.warn('[RuntimeEnv] Invalid JSON in VITE_COLLECTION_OVERRIDES. Using defaults.')
      }
      return {}
    }
  }

  private getEnvString(key: string, defaultValue: string): string {
    // Vite automatically replaces VITE_ prefixed env vars at build time
    const value = import.meta.env[key as keyof ImportMetaEnv]
    return value !== undefined ? String(value) : defaultValue
  }

  private getEnvBool(key: string, defaultValue: boolean): boolean {
    const value = this.getEnvString(key, String(defaultValue))
    return value === 'true' || value === '1' || value === 'yes'
  }

  /**
   * Get the full runtime environment object
   */
  getEnvironment(): RuntimeEnv {
    return { ...this.env }
  }

  /**
   * Check if running in development mode
   */
  isDevelopment(): boolean {
    return this.env.appEnv === 'development'
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return this.env.appEnv === 'production'
  }

  /**
   * Get PocketBase configuration
   */
  getPocketBaseConfig() {
    return { url: this.env.pbUrl }
  }

  /**
   * Get runtime collection name overrides.
   * Example: { "notes": "user_notes" }
   */
  getCollectionOverrides(): Partial<Record<string, string>> {
    return { ...this.env.collectionOverrides }
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.env.features[feature] ?? false
  }

  /**
   * Get log level
   */
  getLogLevel(): string {
    return this.env.logLevel
  }

  /**
   * Should log at specific level
   */
  shouldLog(level: 'debug' | 'info' | 'warn' | 'error'): boolean {
    const levels = ['debug', 'info', 'warn', 'error']
    const currentLevelIndex = levels.indexOf(this.env.logLevel)
    const requestedLevelIndex = levels.indexOf(level)
    return requestedLevelIndex >= currentLevelIndex
  }
}

// Export singleton instance
export const runtimeEnv = new RuntimeEnvironment()

// Export type for use in other services
export type { RuntimeEnv, FeatureFlags }
